import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Music } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const BAR_COUNT = 36;

// Deterministic bar heights from the beat id — so the mini-waveform is stable across renders
// and feels like it belongs to the track, without needing a real waveform asset yet.
function seededWaveform(seed) {
  let h = 2166136261;
  const s = String(seed || "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  const bars = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    h = (h * 1664525 + 1013904223) >>> 0;
    // Envelope: a gentle hump in the middle so it looks like a real stem shape.
    const envelope = 0.4 + 0.6 * Math.sin((i / (BAR_COUNT - 1)) * Math.PI);
    const noise = (h % 1000) / 1000;
    bars.push(Math.max(0.12, envelope * (0.55 + 0.45 * noise)));
  }
  return bars;
}

function MiniWaveform({ seed }) {
  const bars = seededWaveform(seed);
  return (
    <svg
      viewBox={`0 0 ${BAR_COUNT * 4} 32`}
      className="h-8 w-full"
      aria-hidden
      data-testid="pocket-preview-waveform"
    >
      {bars.map((v, i) => {
        const height = Math.max(2, v * 28);
        return (
          <rect
            key={i}
            x={i * 4 + 1}
            y={(32 - height) / 2}
            width={2}
            height={height}
            rx={1}
            className="fill-emerald-300/80"
          />
        );
      })}
    </svg>
  );
}

function PriceLabel({ prices }) {
  if (!prices) return null;
  const nums = Object.values(prices).map(Number).filter((n) => !Number.isNaN(n) && n > 0);
  if (!nums.length) return <span className="text-emerald-300">Offer only</span>;
  const low = Math.min(...nums);
  return <span className="text-emerald-300">from ${low.toFixed(2)}</span>;
}

/**
 * A hover card that fetches the top-matching beat for a pocket and renders
 * cover + title + mini-waveform + BPM/key/genre line. Rendered via portal so it
 * floats cleanly above any overflow-hidden parent.
 */
export function PocketPreview({ anchorRef, search, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: "bottom" });
  const cardRef = useRef(null);

  // Position the portal relative to the anchor every render tick.
  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const CARD_W = 300;
    const CARD_H = 160;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement = spaceBelow > CARD_H + 20 ? "bottom" : "top";
    const top = placement === "bottom" ? rect.bottom + 8 : rect.top - CARD_H - 8;
    let left = rect.left + rect.width / 2 - CARD_W / 2;
    left = Math.max(12, Math.min(window.innerWidth - CARD_W - 12, left));
    setPos({ top, left, placement });
  }, [anchorRef]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (search.genre && search.genre !== "All") params.set("genre", search.genre);
    if (search.bpm && search.bpm !== "all") params.set("bpm", search.bpm);
    if (search.key && search.key !== "all") params.set("key", search.key);
    api
      .get(`/saved-searches/match?${params.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search.genre, search.bpm, search.key]);

  const match = data?.match;

  return createPortal(
    <div
      ref={cardRef}
      role="tooltip"
      data-testid="pocket-preview-card"
      style={{ top: pos.top, left: pos.left, width: 300 }}
      className="fixed z-[80] rounded-2xl border border-emerald-300/30 bg-[#0e0e12]/95 p-3 shadow-2xl backdrop-blur"
      onMouseLeave={onClose}
    >
      {loading ? (
        <div className="flex h-24 items-center justify-center text-zinc-500">
          <Loader2 size={14} className="animate-spin" />
        </div>
      ) : !match ? (
        <div className="flex h-24 flex-col items-center justify-center gap-1 text-center text-zinc-500" data-testid="pocket-preview-empty">
          <Music size={16} className="text-zinc-600" />
          <p className="text-[11px]">No beats in that pocket yet — we'll text you first when one drops.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            {match.cover_url ? (
              <img
                src={fileUrl(match.cover_url)}
                alt=""
                className="h-14 w-14 shrink-0 rounded-xl object-cover"
                data-testid="pocket-preview-cover"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-300/10">
                <Music size={18} className="text-emerald-300" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white" data-testid="pocket-preview-title">
                {match.title}
              </p>
              <p className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                {match.genre || "Beat"} · {match.bpm || "?"} BPM · {match.musical_key || "—"}
              </p>
              <p className="mt-0.5 text-[10px] font-bold">
                <PriceLabel prices={match.prices} />
              </p>
            </div>
          </div>
          <div className="mt-2.5">
            <MiniWaveform seed={match.id} />
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
